import React, { useState, useEffect } from 'react';
import { 
  Plus,
  Calendar,
  Image,
  Eye,
  EyeOff,
  AlertCircle,
  Loader,
  X,
  User,
  LogOut,
  ArrowLeft,
  Trash2,
  Upload,
  ImageIcon,
  Edit
} from 'lucide-react';

const PromotionManagementComponent = ({ userData, onLogout, onBackToMenu }) => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Helper function to format date for datetime-local input
  const getDefaultStartDate = () => {
    const now = new Date();
    return now.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM format
  };

  const getDefaultEndDate = () => {
    const now = new Date();
    const oneYearLater = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate(), now.getHours(), now.getMinutes());
    return oneYearLater.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM format
  };

  const [formData, setFormData] = useState({
    PromotionName: '',
    ImageURL: '',
    Description: '',
    StartDate: getDefaultStartDate(),
    EndDate: getDefaultEndDate(),
    IsActive: true,
    DisplayOrder: 1
  });

  // Delete functionality state
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deletingPromotion, setDeletingPromotion] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Edit functionality state
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editFormError, setEditFormError] = useState('');
  const [editUploadStep, setEditUploadStep] = useState('');
  const [editSelectedImage, setEditSelectedImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editFormData, setEditFormData] = useState({
    PromotionName: '',
    ImageURL: '',
    Description: '',
    StartDate: '',
    EndDate: '',
    IsActive: true,
    DisplayOrder: 1
  });

  // Image upload state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadStep, setUploadStep] = useState('');

  useEffect(() => {
    fetchPromotions();
  }, []);

  // ImgProxy helper function - Using original URL for stability
  const getProxyImageUrl = (imageUrl, options = 'rs:fit:200:200/q:80', context = 'unknown') => {
    if (!imageUrl || imageUrl.trim() === '') {
      return null;
    }
    
    // Use original URL directly since proxy is causing issues
    return imageUrl;
    
    // ImgProxy code (disabled for stability):
    /*
    try {
      // Standard base64 encoding as per imgproxy documentation
      const encodedUrl = btoa(imageUrl);
      const proxyUrl = `https://apisaleeasy.psisat.com/imgproxy/${options}/${encodedUrl}`;
      
      console.log(`🖼️ [${context}] ImgProxy URL:`, proxyUrl);
      
      return proxyUrl;
    } catch (error) {
      console.error(`[${context}] Error encoding image URL:`, error);
      return imageUrl; // Fallback to original URL
    }
    */
  };

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('https://apisaleeasy.psisat.com/api/promotions/admin', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status && result.data) {
          console.log('✅ Loaded', result.data.length, 'promotions');
          setPromotions(result.data);
        } else {
          console.error('❌ API Response Error:', result);
          setError('ไม่สามารถโหลดข้อมูลโปรโมชั่นได้');
        }
      } else {
        console.error('❌ HTTP Error:', response.status, response.statusText);
        setError('เกิดข้อผิดพลาดในการเชื่อมต่อ API');
      }
    } catch (err) {
      console.error('Error fetching promotions:', err);
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };


  const handleCreatePromotion = () => {
    if (!userData || (!userData.username && !userData.fullName)) {
      alert('กรุณาเข้าสู่ระบบก่อนสร้างโปรโมชั่น');
      return;
    }
    const maxOrder = promotions.length > 0
      ? Math.max(...promotions.map(p => p.DisplayOrder || 0))
      : 0;
    setFormData(prev => ({ ...prev, DisplayOrder: maxOrder + 1 }));
    setShowCreatePopup(true);
  };

  // Edit functions
  const handleEditClick = (promotion) => {
    if (!userData || (!userData.username && !userData.fullName)) {
      alert('กรุณาเข้าสู่ระบบก่อนแก้ไขโปรโมชั่น');
      return;
    }
    setEditingPromotion(promotion);
    setEditFormData({
      PromotionName: promotion.PromotionName,
      ImageURL: promotion.ImageURL || '',
      Description: promotion.Description,
      StartDate: promotion.StartDate.slice(0, 16), // Convert to datetime-local format
      EndDate: promotion.EndDate.slice(0, 16), // Convert to datetime-local format
      IsActive: promotion.IsActive,
      DisplayOrder: promotion.DisplayOrder
    });
    setEditFormError('');
    setEditSelectedImage(null);
    setEditImagePreview(null);
    setEditUploadStep('');
    setShowEditPopup(true);
  };

  const closeEditPopup = () => {
    setShowEditPopup(false);
    setEditingPromotion(null);
    setEditFormData({
      PromotionName: '',
      ImageURL: '',
      Description: '',
      StartDate: '',
      EndDate: '',
      IsActive: true,
      DisplayOrder: 1
    });
    setEditFormError('');
    setEditSelectedImage(null);
    setEditImagePreview(null);
    setEditUploadStep('');
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEditImageSelect = (e) => {
    const file = e.target.files[0];
    console.log('🔵 Edit Image selected:', file);
    
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        console.error('🔴 Invalid file type:', file.type);
        setEditFormError('กรุณาเลือกไฟล์รูปภาพที่ถูกต้อง (JPG, PNG, GIF, WebP)');
        return;
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        console.error('🔴 File too large:', file.size);
        setEditFormError('ไฟล์รูปภาพต้องมีขนาดไม่เกิน 5MB');
        return;
      }

      console.log('✅ Edit File validation passed');
      setEditSelectedImage(file);
      setEditFormError('');

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('✅ Edit Image preview created');
        setEditImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      console.log('🔴 No edit file selected');
    }
  };

  const removeEditImage = () => {
    setEditSelectedImage(null);
    setEditImagePreview(null);
    // Reset file input
    const fileInput = document.getElementById('editImageUpload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const validateEditForm = () => {
    if (!editFormData.PromotionName.trim()) {
      setEditFormError('กรุณากรอกชื่อโปรโมชั่น');
      return false;
    }
    if (!editFormData.Description.trim()) {
      setEditFormError('กรุณากรอกคำอธิบายโปรโมชั่น');
      return false;
    }
    if (!editFormData.StartDate) {
      setEditFormError('กรุณาเลือกวันที่เริ่มต้น');
      return false;
    }
    if (!editFormData.EndDate) {
      setEditFormError('กรุณาเลือกวันที่สิ้นสุด');
      return false;
    }
    if (new Date(editFormData.StartDate) >= new Date(editFormData.EndDate)) {
      setEditFormError('วันที่เริ่มต้นต้องน้อยกว่าวันที่สิ้นสุด');
      return false;
    }
    if (editFormData.DisplayOrder < 1) {
      setEditFormError('ลำดับการแสดงต้องมากกว่า 0');
      return false;
    }
    return true;
  };

  // Update promotion API function
  const updatePromotion = async (promotionId, promotionData) => {
    const response = await fetch(`https://apisaleeasy.psisat.com/api/promotions/${promotionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        promotionName: promotionData.PromotionName,
        imageURL: promotionData.ImageURL,
        description: promotionData.Description,
        startDate: new Date(promotionData.StartDate).toISOString(),
        endDate: new Date(promotionData.EndDate).toISOString(),
        isActive: promotionData.IsActive,
        displayOrder: promotionData.DisplayOrder,
        updatedBy: promotionData.UpdatedBy
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update promotion');
    }

    const result = await response.json();
    
    if (!result.status) {
      throw new Error(result.result_desc || 'Promotion update failed');
    }

    return result;
  };

  const handleSubmitEditPromotion = async () => {
    if (!userData || (!userData.username && !userData.fullName)) {
      setEditFormError('กรุณาเข้าสู่ระบบก่อนแก้ไขโปรโมชั่น');
      return;
    }

    if (!validateEditForm() || !editingPromotion) {
      return;
    }

    setEditLoading(true);
    setEditFormError('');

    try {
      let imageURL = editFormData.ImageURL; // Start with URL from input field

      // Step 1: Upload image if a file was selected
      if (editSelectedImage) {
        try {
          setEditFormError(''); // Clear any previous errors
          setEditUploadStep('กำลังอัปโหลดรูปภาพ...');
          console.log('Uploading edit image...');
          imageURL = await uploadImage(editSelectedImage);
          console.log('Edit image uploaded successfully:', imageURL);
        } catch (uploadError) {
          console.error('Edit image upload failed:', uploadError);
          
          let errorMessage = uploadError.message;
          
          // Handle specific error types
          if (errorMessage.includes('File content validation failed')) {
            setEditFormError('ไฟล์รูปภาพไม่ผ่านการตรวจสอบความปลอดภัย กรุณาเลือกไฟล์รูปภาพอื่น หรือลองแปลงเป็นรูปแบบ PNG');
          } else if (errorMessage.includes('400')) {
            setEditFormError('เกิดข้อผิดพลาด: ' + errorMessage + ' - กรุณาลองอีกครั้งในอีกสักครู่');
          } else {
            setEditFormError('เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ: ' + errorMessage);
          }
          
          setEditLoading(false);
          setEditUploadStep('');
          return;
        }
      }

      // Step 2: Update promotion with the image URL
      setEditUploadStep('กำลังอัปเดตโปรโมชั่น...');
      const promotionData = {
        ...editFormData,
        ImageURL: imageURL,
        UpdatedBy: userData?.fullName || userData?.username || 'Unknown User'
      };

      console.log('Updating promotion with data:', promotionData);

      const updatedPromotion = await updatePromotion(editingPromotion.PromotionID, promotionData);
      console.log('Promotion updated successfully:', updatedPromotion);

      // Success
      setEditLoading(false);
      setEditUploadStep('');
      closeEditPopup();
      fetchPromotions(); // Refresh the list
      alert('อัปเดตโปรโมชั่นสำเร็จ');

    } catch (error) {
      console.error('Error in promotion update process:', error);
      setEditFormError('เกิดข้อผิดพลาดในการอัปเดตโปรโมชั่น: ' + error.message);
      setEditLoading(false);
      setEditUploadStep('');
    }
  };

  // Edit drag and drop handlers
  const handleEditDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEditDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEditDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEditDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      // Simulate file input change
      const mockEvent = {
        target: {
          files: files
        }
      };
      handleEditImageSelect(mockEvent);
    }
  };

  const closeCreatePopup = () => {
    setShowCreatePopup(false);
    setFormData({
      PromotionName: '',
      ImageURL: '',
      Description: '',
      StartDate: getDefaultStartDate(),
      EndDate: getDefaultEndDate(),
      IsActive: true,
      DisplayOrder: 1
    });
    setFormError('');
    setSelectedImage(null);
    setImagePreview(null);
    setUploadStep('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };


  // Image handling functions
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    console.log('🔵 Image selected:', file);
    
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        console.error('🔴 Invalid file type:', file.type);
        setFormError('กรุณาเลือกไฟล์รูปภาพที่ถูกต้อง (JPG, PNG, GIF, WebP)');
        return;
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        console.error('🔴 File too large:', file.size);
        setFormError('ไฟล์รูปภาพต้องมีขนาดไม่เกิน 5MB');
        return;
      }

      console.log('✅ File validation passed');
      setSelectedImage(file);
      setFormError('');

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('✅ Image preview created');
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      console.log('🔴 No file selected');
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    // Reset file input
    const fileInput = document.getElementById('imageUpload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      // Simulate file input change
      const mockEvent = {
        target: {
          files: files
        }
      };
      handleImageSelect(mockEvent);
    }
  };

  const validateForm = () => {
    if (!formData.PromotionName.trim()) {
      setFormError('กรุณากรอกชื่อโปรโมชั่น');
      return false;
    }
    if (!formData.Description.trim()) {
      setFormError('กรุณากรอกคำอธิบายโปรโมชั่น');
      return false;
    }
    if (!formData.StartDate) {
      setFormError('กรุณาเลือกวันที่เริ่มต้น');
      return false;
    }
    if (!formData.EndDate) {
      setFormError('กรุณาเลือกวันที่สิ้นสุด');
      return false;
    }
    if (new Date(formData.StartDate) >= new Date(formData.EndDate)) {
      setFormError('วันที่เริ่มต้นต้องน้อยกว่าวันที่สิ้นสุด');
      return false;
    }
    if (formData.DisplayOrder < 1) {
      setFormError('ลำดับการแสดงต้องมากกว่า 0');
      return false;
    }
    return true;
  };

  // Image upload API function
  const uploadImage = async (imageFile) => {
    console.log('🔵 Starting image upload:', {
      fileName: imageFile.name,
      fileSize: imageFile.size,
      fileType: imageFile.type
    });
    
    const formData = new FormData();
    formData.append('image', imageFile);

    console.log('🔵 Sending request to upload API...');
    const response = await fetch('https://apisaleeasy.psisat.com/api/images/upload', {
      method: 'POST',
      body: formData
    });

    console.log('🔵 Upload response status:', response.status);
    
    const result = await response.json();
    console.log('🔵 Upload result:', result);
    
    if (!response.ok) {
      console.error('🔴 Upload failed:', {
        status: response.status,
        result: result
      });
      throw new Error(`Failed to upload image: ${response.status} - ${result.message || JSON.stringify(result)}`);
    }
    
    if (!result.success) {
      throw new Error(result.message || 'Image upload failed');
    }

    console.log('✅ Image uploaded successfully:', result.data.fullUrl);
    return result.data.fullUrl;
  };

  // Create promotion API function
  const createPromotion = async (promotionData) => {
    const response = await fetch('https://apisaleeasy.psisat.com/api/promotions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        promotionName: promotionData.PromotionName,
        imageURL: promotionData.ImageURL,
        description: promotionData.Description,
        startDate: promotionData.StartDate,
        endDate: promotionData.EndDate,
        isActive: promotionData.IsActive,
        createdBy: promotionData.CreatedBy,
        displayOrder: promotionData.DisplayOrder
      })
    });

    if (!response.ok) {
      throw new Error('Failed to create promotion');
    }

    const result = await response.json();
    
    if (!result.status) {
      throw new Error(result.result_desc || 'Promotion creation failed');
    }

    return result.data;
  };

  const handleSubmitPromotion = async () => {
    if (!userData || (!userData.username && !userData.fullName)) {
      setFormError('กรุณาเข้าสู่ระบบก่อนสร้างโปรโมชั่น');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setCreateLoading(true);
    setFormError('');

    try {
      let imageURL = formData.ImageURL; // Start with URL from input field

      // Step 1: Upload image if a file was selected
      if (selectedImage) {
        try {
          setFormError(''); // Clear any previous errors
          setUploadStep('กำลังอัปโหลดรูปภาพ...');
          console.log('Uploading image...');
          imageURL = await uploadImage(selectedImage);
          console.log('Image uploaded successfully:', imageURL);
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          
          let errorMessage = uploadError.message;
          
          // Handle specific error types
          if (errorMessage.includes('File content validation failed')) {
            setFormError('ไฟล์รูปภาพไม่ผ่านการตรวจสอบความปลอดภัย กรุณาเลือกไฟล์รูปภาพอื่น หรือลองแปลงเป็นรูปแบบ PNG');
          } else if (errorMessage.includes('400')) {
            setFormError('เกิดข้อผิดพลาด: ' + errorMessage + ' - กรุณาลองอีกครั้งในอีกสักครู่');
          } else {
            setFormError('เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ: ' + errorMessage);
          }
          
          setCreateLoading(false);
          setUploadStep('');
          return;
        }
      }

      // Step 2: Create promotion with the image URL
      setUploadStep('กำลังสร้างโปรโมชั่น...');
      const promotionData = {
        ...formData,
        ImageURL: imageURL,
        CreatedBy: userData?.fullName || userData?.username || 'Unknown User'
      };

      console.log('Creating promotion with data:', promotionData);
      
      const createdPromotion = await createPromotion(promotionData);
      console.log('Promotion created successfully:', createdPromotion);

      // Success
      setCreateLoading(false);
      setUploadStep('');
      closeCreatePopup();
      fetchPromotions(); // Refresh the list
      alert('สร้างโปรโมชั่นสำเร็จ');

    } catch (error) {
      console.error('Error in promotion creation process:', error);
      setFormError('เกิดข้อผิดพลาดในการสร้างโปรโมชั่น: ' + error.message);
      setCreateLoading(false);
      setUploadStep('');
    }
  };

  // Delete functions
  const handleDeleteClick = (promotion) => {
    setDeletingPromotion(promotion);
    setShowDeletePopup(true);
  };

  const closeDeletePopup = () => {
    setShowDeletePopup(false);
    setDeletingPromotion(null);
    setDeleteLoading(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPromotion) return;

    setDeleteLoading(true);

    try {
      const response = await fetch(`https://apisaleeasy.psisat.com/api/promotions/${deletingPromotion.PromotionID}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status) {
          // Success
          setDeleteLoading(false);
          closeDeletePopup();
          fetchPromotions(); // Refresh the list
          alert('ลบโปรโมชั่นสำเร็จ');
        } else {
          throw new Error(result.result_desc || 'Failed to delete promotion');
        }
      } else {
        throw new Error('Failed to delete promotion');
      }
    } catch (error) {
      console.error('Error deleting promotion:', error);
      setDeleteLoading(false);
      alert('เกิดข้อผิดพลาดในการลบโปรโมชั่น');
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">กำลังโหลดข้อมูลโปรโมชั่น...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Top Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={onBackToMenu}
              className="flex items-center gap-2 px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              กลับหน้าหลัก
            </button>
            <div className="h-6 w-px bg-gray-300"></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">จัดการโปรโมชั่น</h1>
              <p className="text-gray-600">ระบบจัดการโปรโมชั่นสินค้า (หน้าลับ)</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* User Info */}
            <div className="flex items-center gap-2 text-gray-700">
              <User className="w-5 h-5" />
              <span className="font-medium">{userData?.fullName || userData?.firstName}</span>
            </div>
            
            {/* Logout Button */}
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">ออกจากระบบ</span>
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Action Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">รายการโปรโมชั่น</h2>
              <p className="text-gray-600">จัดการและแสดงรายการโปรโมชั่นทั้งหมด</p>
            </div>
            <button
              onClick={handleCreatePromotion}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              สร้างโปรโมชั่น
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div>
                <h4 className="text-red-800 font-medium">เกิดข้อผิดพลาด</h4>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
              <button
                onClick={() => setError('')}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Promotions Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    รูปภาพ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ชื่อโปรโมชั่น
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    วันที่เริ่มต้น
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    วันที่สิ้นสุด
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    สถานะ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ลำดับการแสดง
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ผู้สร้าง
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    วันที่สร้าง
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    การจัดการ
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {promotions.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <Image className="w-12 h-12 text-gray-300 mb-4" />
                      <p className="text-lg font-medium">ไม่มีข้อมูลโปรโมชั่น</p>
                      <p className="text-sm">ยังไม่มีโปรโมชั่นในระบบ</p>
                    </div>
                  </td>
                </tr>
              ) : (
                promotions.map((promotion) => (
                  <tr key={promotion.PromotionID} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {promotion.PromotionID}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {promotion.ImageURL ? (
                        <img
                          src={getProxyImageUrl(promotion.ImageURL, 'rs:crop:64:64/q:90', `Table-Row-${promotion.PromotionID}`)}
                          alt={promotion.PromotionName}
                          className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            console.log(`🚫 [Table-Row-${promotion.PromotionID}] Image failed:`, {
                              src: e.target.src,
                              promotion: promotion.PromotionName,
                              originalURL: promotion.ImageURL
                            });
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center"
                        style={{ display: promotion.ImageURL ? 'none' : 'flex' }}
                      >
                        <Image className="w-6 h-6 text-gray-400" />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <div className="max-w-xs">
                        {promotion.PromotionName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(promotion.StartDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(promotion.EndDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {promotion.IsActive ? (
                          <>
                            <Eye className="w-4 h-4 text-green-600" />
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              เปิดใช้งาน
                            </span>
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-4 h-4 text-gray-400" />
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              ปิดใช้งาน
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                        {promotion.DisplayOrder}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {promotion.CreatedBy}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(promotion.CreatedDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleEditClick(promotion)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                          title="แก้ไขโปรโมชั่น"
                          disabled={editLoading || deleteLoading}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(promotion)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                          title="ลบโปรโมชั่น"
                          disabled={deleteLoading || editLoading}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Card */}
        {promotions.length > 0 && (
          <div className="mt-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-blue-800 font-medium">สรุปข้อมูลโปรโมชั่น</h3>
                  <p className="text-blue-600 text-sm">
                    ทั้งหมด {promotions.length} โปรโมชั่น | 
                    เปิดใช้งาน {promotions.filter(p => p.IsActive).length} โปรโมชั่น | 
                    ปิดใช้งาน {promotions.filter(p => !p.IsActive).length} โปรโมชั่น
                  </p>
                </div>
                <button
                  onClick={fetchPromotions}
                  disabled={loading}
                  className="flex items-center gap-2 px-3 py-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {loading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <>🔄 รีเฟรช</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Promotion Popup */}
        {showCreatePopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Plus className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">สร้างโปรโมชั่นใหม่</h3>
                      <p className="text-sm text-gray-500">กรอกข้อมูลโปรโมชั่นที่ต้องการสร้าง</p>
                    </div>
                  </div>
                  <button
                    onClick={closeCreatePopup}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                    disabled={createLoading}
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-6">
                {/* Error Alert */}
                {formError && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div>
                      <h4 className="text-red-800 font-medium">เกิดข้อผิดพลาด</h4>
                      <p className="text-red-600 text-sm">{formError}</p>
                    </div>
                    <button
                      onClick={() => setFormError('')}
                      className="ml-auto text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <form onSubmit={(e) => { e.preventDefault(); handleSubmitPromotion(); }} className="space-y-6">
                  {/* Promotion Name */}
                  <div>
                    <label htmlFor="PromotionName" className="block text-sm font-medium text-gray-700 mb-2">
                      ชื่อโปรโมชั่น <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="PromotionName"
                      name="PromotionName"
                      value={formData.PromotionName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="เช่น Solar Cell โปรโมชั่นปีใหม่ 2026"
                      disabled={createLoading}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="Description" className="block text-sm font-medium text-gray-700 mb-2">
                      คำอธิบายโปรโมชั่น <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="Description"
                      name="Description"
                      value={formData.Description}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="🎉 ลดราคาพิเศษ 20% สำหรับระบบโซลาร์เซลล์ทุกรุ่น รับปีใหม่ 2026! ประหยัดค่าไฟฟ้า พร้อมรับประกัน 25 ปี ติดตั้งฟรีทั่วประเทศ"
                      disabled={createLoading}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {formData.Description.length} ตัวอักษร
                    </p>
                  </div>

                  {/* Date Range */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="StartDate" className="block text-sm font-medium text-gray-700 mb-2">
                        วันที่เริ่มต้น <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        id="StartDate"
                        name="StartDate"
                        value={formData.StartDate}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={createLoading}
                      />
                    </div>
                    <div>
                      <label htmlFor="EndDate" className="block text-sm font-medium text-gray-700 mb-2">
                        วันที่สิ้นสุด <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        id="EndDate"
                        name="EndDate"
                        value={formData.EndDate}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={createLoading}
                      />
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      รูปภาพโปรโมชั่น
                    </label>
                    
                    {/* Image Preview */}
                    {imagePreview ? (
                      <div className="mb-4">
                        <div className="relative inline-block">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={removeImage}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                            disabled={createLoading}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <p className="text-xs text-green-600 mt-2">
                          ✅ เลือกรูปภาพแล้ว: {selectedImage?.name}
                        </p>
                      </div>
                    ) : (
                      <div className="mb-4">
                        <label
                          htmlFor="imageUpload"
                          className="cursor-pointer block w-full"
                        >
                          <div 
                            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors"
                            onDragOver={handleDragOver}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                          >
                            <div className="flex flex-col items-center gap-3">
                              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                <ImageIcon className="w-6 h-6 text-gray-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700">
                                  คลิกเพื่อเลือกรูปภาพ
                                </p>
                                <p className="text-xs text-gray-500">
                                  หรือลากและวางรูปภาพที่นี่
                                </p>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-400">
                                <Upload className="w-3 h-3" />
                                <span>JPG, PNG, GIF, WebP (สูงสุด 5MB)</span>
                              </div>
                            </div>
                          </div>
                        </label>
                        <input
                          type="file"
                          id="imageUpload"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          onChange={handleImageSelect}
                          className="hidden"
                          disabled={createLoading}
                        />
                      </div>
                    )}

                    {/* URL Input Alternative */}
                    <div className="border-t border-gray-200 pt-4">
                      <label htmlFor="ImageURL" className="block text-xs font-medium text-gray-500 mb-2">
                        หรือใส่ URL รูปภาพ (ทางเลือก)
                      </label>
                      <input
                        type="url"
                        id="ImageURL"
                        name="ImageURL"
                        value={formData.ImageURL}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="https://example.com/images/promotion.jpg"
                        disabled={createLoading}
                      />
                    </div>
                    
                    <p className="mt-2 text-xs text-gray-500">
                      💡 ไม่บังคับ - สามารถเลือกรูปภาพหรือใส่ URL หรือเว้นว่างไว้ได้<br/>
                      ⚠️ หากไฟล์ไม่ผ่านการตรวจสอบความปลอดภัย แนะนำให้แปลงเป็น PNG หรือใช้รูปภาพอื่น
                    </p>
                  </div>

                  {/* Display Order and Status */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="DisplayOrder" className="block text-sm font-medium text-gray-700 mb-2">
                        ลำดับการแสดง
                      </label>
                      <input
                        type="number"
                        id="DisplayOrder"
                        name="DisplayOrder"
                        value={formData.DisplayOrder}
                        onChange={handleInputChange}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={createLoading}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        ยิ่งเลขน้อย แสดงก่อน
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        สถานะการใช้งาน
                      </label>
                      <div className="flex items-center gap-3 h-10">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name="IsActive"
                            checked={formData.IsActive}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            disabled={createLoading}
                          />
                          <span className="text-sm text-gray-700">เปิดใช้งานทันที</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Created By Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">ข้อมูลผู้สร้าง</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">ผู้สร้าง:</span>
                        <span className="ml-2 font-medium">{userData?.fullName || userData?.username || 'Unknown User'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">วันที่สร้าง:</span>
                        <span className="ml-2 font-medium">{formatDate(new Date().toISOString())}</span>
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={closeCreatePopup}
                      className="flex-1 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                      disabled={createLoading}
                    >
                      ยกเลิก
                    </button>
                    <button
                      type="submit"
                      disabled={createLoading}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {createLoading ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          {uploadStep || 'กำลังดำเนินการ...'}
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          สร้างโปรโมชั่น
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Promotion Popup */}
        {showEditPopup && editingPromotion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Edit className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">แก้ไขโปรโมชั่น</h3>
                      <p className="text-sm text-gray-500">แก้ไขข้อมูลโปรโมชั่น ID: {editingPromotion.PromotionID}</p>
                    </div>
                  </div>
                  <button
                    onClick={closeEditPopup}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                    disabled={editLoading}
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-6">
                {/* Error Alert */}
                {editFormError && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div>
                      <h4 className="text-red-800 font-medium">เกิดข้อผิดพลาด</h4>
                      <p className="text-red-600 text-sm">{editFormError}</p>
                    </div>
                    <button
                      onClick={() => setEditFormError('')}
                      className="ml-auto text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <form onSubmit={(e) => { e.preventDefault(); handleSubmitEditPromotion(); }} className="space-y-6">
                  {/* Promotion Name */}
                  <div>
                    <label htmlFor="EditPromotionName" className="block text-sm font-medium text-gray-700 mb-2">
                      ชื่อโปรโมชั่น <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="EditPromotionName"
                      name="PromotionName"
                      value={editFormData.PromotionName}
                      onChange={handleEditInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="เช่น Solar Cell โปรโมชั่นปีใหม่ 2026"
                      disabled={editLoading}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="EditDescription" className="block text-sm font-medium text-gray-700 mb-2">
                      คำอธิบายโปรโมชั่น <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="EditDescription"
                      name="Description"
                      value={editFormData.Description}
                      onChange={handleEditInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="🎉 ลดราคาพิเศษ 20% สำหรับระบบโซลาร์เซลล์ทุกรุ่น รับปีใหม่ 2026! ประหยัดค่าไฟฟ้า พร้อมรับประกัน 25 ปี ติดตั้งฟรีทั่วประเทศ"
                      disabled={editLoading}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {editFormData.Description.length} ตัวอักษร
                    </p>
                  </div>

                  {/* Date Range */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="EditStartDate" className="block text-sm font-medium text-gray-700 mb-2">
                        วันที่เริ่มต้น <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        id="EditStartDate"
                        name="StartDate"
                        value={editFormData.StartDate}
                        onChange={handleEditInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={editLoading}
                      />
                    </div>
                    <div>
                      <label htmlFor="EditEndDate" className="block text-sm font-medium text-gray-700 mb-2">
                        วันที่สิ้นสุด <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        id="EditEndDate"
                        name="EndDate"
                        value={editFormData.EndDate}
                        onChange={handleEditInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={editLoading}
                      />
                    </div>
                  </div>

                  {/* Current Image Display */}
                  {editingPromotion.ImageURL && !editImagePreview && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        รูปภาพปัจจุบัน
                      </label>
                      <div className="mb-4">
                        <img
                          src={getProxyImageUrl(editingPromotion.ImageURL, 'rs:fit:200:200/q:80', `Edit-Current-${editingPromotion.PromotionID}`)}
                          alt={editingPromotion.PromotionName}
                          className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            console.log(`🚫 [Edit-Current-${editingPromotion.PromotionID}] Image failed`);
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div 
                          className="w-32 h-32 bg-gray-100 rounded-lg border-2 border-gray-200 flex items-center justify-center"
                          style={{ display: 'none' }}
                        >
                          <Image className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          รูปภาพปัจจุบัน
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      เปลี่ยนรูปภาพโปรโมชั่น
                    </label>
                    
                    {/* Image Preview */}
                    {editImagePreview ? (
                      <div className="mb-4">
                        <div className="relative inline-block">
                          <img
                            src={editImagePreview}
                            alt="Preview"
                            className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={removeEditImage}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                            disabled={editLoading}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <p className="text-xs text-green-600 mt-2">
                          ✅ เลือกรูปภาพใหม่แล้ว: {editSelectedImage?.name}
                        </p>
                      </div>
                    ) : (
                      <div className="mb-4">
                        <label
                          htmlFor="editImageUpload"
                          className="cursor-pointer block w-full"
                        >
                          <div 
                            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors"
                            onDragOver={handleEditDragOver}
                            onDragEnter={handleEditDragEnter}
                            onDragLeave={handleEditDragLeave}
                            onDrop={handleEditDrop}
                          >
                            <div className="flex flex-col items-center gap-3">
                              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                <ImageIcon className="w-6 h-6 text-gray-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700">
                                  คลิกเพื่อเลือกรูปภาพใหม่
                                </p>
                                <p className="text-xs text-gray-500">
                                  หรือลากและวางรูปภาพที่นี่
                                </p>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-400">
                                <Upload className="w-3 h-3" />
                                <span>JPG, PNG, GIF, WebP (สูงสุด 5MB)</span>
                              </div>
                            </div>
                          </div>
                        </label>
                        <input
                          type="file"
                          id="editImageUpload"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          onChange={handleEditImageSelect}
                          className="hidden"
                          disabled={editLoading}
                        />
                      </div>
                    )}

                    {/* URL Input Alternative */}
                    <div className="border-t border-gray-200 pt-4">
                      <label htmlFor="EditImageURL" className="block text-xs font-medium text-gray-500 mb-2">
                        หรือใส่ URL รูปภาพใหม่ (ทางเลือก)
                      </label>
                      <input
                        type="url"
                        id="EditImageURL"
                        name="ImageURL"
                        value={editFormData.ImageURL}
                        onChange={handleEditInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="https://example.com/images/promotion.jpg"
                        disabled={editLoading}
                      />
                    </div>
                    
                    <p className="mt-2 text-xs text-gray-500">
                      💡 ไม่บังคับ - หากไม่เลือกรูปใหม่ จะใช้รูปเดิม<br/>
                      ⚠️ หากไฟล์ไม่ผ่านการตรวจสอบความปลอดภัย แนะนำให้แปลงเป็น PNG หรือใช้รูปภาพอื่น
                    </p>
                  </div>

                  {/* Display Order and Status */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="EditDisplayOrder" className="block text-sm font-medium text-gray-700 mb-2">
                        ลำดับการแสดง
                      </label>
                      <input
                        type="number"
                        id="EditDisplayOrder"
                        name="DisplayOrder"
                        value={editFormData.DisplayOrder}
                        onChange={handleEditInputChange}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={editLoading}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        ยิ่งเลขน้อย แสดงก่อน
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        สถานะการใช้งาน
                      </label>
                      <div className="flex items-center gap-3 h-10">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name="IsActive"
                            checked={editFormData.IsActive}
                            onChange={handleEditInputChange}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            disabled={editLoading}
                          />
                          <span className="text-sm text-gray-700">เปิดใช้งาน</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Original Promotion Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">ข้อมูลโปรโมชั่นเดิม</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">ผู้สร้าง:</span>
                        <span className="ml-2 font-medium">{editingPromotion.CreatedBy}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">วันที่สร้าง:</span>
                        <span className="ml-2 font-medium">{formatDate(editingPromotion.CreatedDate)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={closeEditPopup}
                      className="flex-1 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                      disabled={editLoading}
                    >
                      ยกเลิก
                    </button>
                    <button
                      type="submit"
                      disabled={editLoading}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {editLoading ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          {editUploadStep || 'กำลังดำเนินการ...'}
                        </>
                      ) : (
                        <>
                          <Edit className="w-4 h-4" />
                          บันทึกการแก้ไข
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Popup */}
        {showDeletePopup && deletingPromotion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="text-center">
                {/* Icon */}
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
                
                {/* Title */}
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  ยืนยันการลบโปรโมชั่น
                </h3>
                
                {/* Description */}
                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    คุณต้องการลบโปรโมชั่นหรือไม่?
                  </p>
                  
                  {/* Promotion Details */}
                  <div className="bg-gray-50 rounded-lg p-4 text-left">
                    <div className="flex items-start gap-3">
                      {deletingPromotion.ImageURL ? (
                        <img
                          src={getProxyImageUrl(deletingPromotion.ImageURL, 'rs:crop:48:48/q:90', `Delete-Modal-${deletingPromotion.PromotionID}`)}
                          alt={deletingPromotion.PromotionName}
                          className="w-12 h-12 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            console.log(`🚫 [Delete-Modal-${deletingPromotion.PromotionID}] Image failed, showing placeholder`);
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-12 h-12 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0"
                        style={{ display: deletingPromotion.ImageURL ? 'none' : 'flex' }}
                      >
                        <Image className="w-6 h-6 text-gray-400" />
                      </div>
                      
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {deletingPromotion.PromotionName}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                          ID: {deletingPromotion.PromotionID}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          {deletingPromotion.IsActive ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              เปิดใช้งาน
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              ปิดใช้งาน
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm font-medium flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      การดำเนินการนี้ไม่สามารถย้อนกลับได้
                    </p>
                  </div>
                </div>
                
                {/* Buttons */}
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={closeDeletePopup}
                    className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                    disabled={deleteLoading}
                  >
                    ยกเลิก
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={deleteLoading}
                    className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleteLoading ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        กำลังลบ...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        ใช่, ลบโปรโมชั่น
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotionManagementComponent;